import React, { memo } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import isNil from 'lodash/isNil';
import { lazyForPaint } from 'react-loosely-lazy';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useFieldValue } from '@atlassian/jira-issue-field-base';
import type { PriorityChipField as PriorityChipFieldType } from '@atlassian/jira-issue-field-priority-chip';
import { PriorityChipFieldBoundary } from '@atlassian/jira-issue-field-priority-chip/async';
import { PRIORITY_TYPE } from '@atlassian/jira-platform-field-config';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics';
import SLAExperienceTracker from '../../../../sla-experience-tracker';
import PriorityFieldStatic from './priority-field-static';
import type { PriorityFieldProps } from './types';

const PriorityChipFieldLazy = lazyForPaint<typeof PriorityChipFieldType>(() =>
	import(
		/* webpackChunkName: "async-issue-field-priority" */ '@atlassian/jira-issue-field-priority-chip'
	).then(({ PriorityChipField }) => PriorityChipField),
);

const PriorityField = ({
	isUserAtLeastMember,
	shouldRenderRichCard,
	issueKey,
	shouldRenderColumn,
}: PriorityFieldProps) => {
	const [priorityFieldValue] = useFieldValue({
		issueKey,
		fieldKey: PRIORITY_TYPE,
	});

	// Do not render the priority column if no card on backlog has priority
	if (
		!shouldRenderColumn &&
		(isNil(priorityFieldValue) || !priorityFieldValue.name || !priorityFieldValue.iconUrl)
	) {
		return null;
	}
	const hasPriority =
		!isNil(priorityFieldValue) &&
		!isNil(priorityFieldValue.name) &&
		!isNil(priorityFieldValue.iconUrl);

	const priorityFieldStatic = (
		<StaticPriorityFieldWrapper shouldRenderColumn={shouldRenderColumn}>
			{hasPriority ? (
				<PriorityFieldStatic name={priorityFieldValue.name} iconUrl={priorityFieldValue.iconUrl} />
			) : null}
		</StaticPriorityFieldWrapper>
	);
	if (isUserAtLeastMember && shouldRenderRichCard) {
		return (
			<PriorityChipFieldBoundary packageName={BACKLOG_PACKAGE_NAME} fallback={priorityFieldStatic}>
				<SLAExperienceTracker
					experienceTarget={PRIORITY_TYPE}
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<PriorityFieldWrapper shouldRenderColumn={shouldRenderColumn}>
						{hasPriority ? (
							<ContextualAnalyticsData attributes={{ isInlineEditing: true }}>
								<PriorityChipFieldLazy issueKey={issueKey} />
							</ContextualAnalyticsData>
						) : null}
					</PriorityFieldWrapper>
				</SLAExperienceTracker>
			</PriorityChipFieldBoundary>
		);
	}

	return priorityFieldStatic;
};

export default memo<PriorityFieldProps>(PriorityField);

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PriorityFieldWrapper = styled.div<{
	shouldRenderColumn: boolean;
}>`
	position: relative; /* NOTE: This is needed to ensure the dropdown menu aligned correctly */
	display: flex;
	align-items: center;
	cursor: default;
	min-width: ${({ shouldRenderColumn }) => (shouldRenderColumn ? `${gridSize * 3}px` : null)};
	max-width: ${({ shouldRenderColumn }) => (shouldRenderColumn ? `${gridSize * 3}px` : null)};
	/* Override stylings injected by AK component via JS */
	& > button + div {
		position: absolute !important;
		top: ${token('space.400', '32px')} !important;
		left: auto !important;
		right: ${token('space.negative.025', '-2px')} !important;
		transform: none !important;
	}

	&:focus,
	&:focus-within {
		outline: none;
	}
`;

// empty column for the cards that without priority
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StaticPriorityFieldWrapper = styled2.div<{
	shouldRenderColumn: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>({}, (props) =>
	props.shouldRenderColumn
		? {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				minWidth: `${gridSize * 3}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				maxWidth: `${gridSize * 3}px`,
			}
		: {},
);
